import React from 'react';
import { createRoot } from 'react-dom/client';
import utils from 'utils';
import WINDOW_OBJECT_KEYS from 'constants/windowObjectKeys';
import { SESSION_STORAGE } from 'constants/session';
import { Provider } from 'react-redux';
import Authenticated from 'components/Authenticated';
import useActions from 'hooks/useActions';
import { showSessionTimeoutModal } from "actions/modal/showSessionTimeoutModal";
import { showUpdateEmailAddressModal } from "actions/modal/showUpdateEmailAddressModal";
import EmailUpdatedSuccessModal from '../components/modals/EmailUpdatedSuccessModal';
import UpdateEmailAddressModal from '../components/modals/UpdateEmailAddressModal';
import SessionTimeoutModal from '../components/modals/SessionTimeoutModal';

/**
 * A public init method must exist to be called from uiManager
 * @param {Object} store The redux store for the site
 * @param {HTMLElement} domEl The dom element related to this module
 */
export default {
  init(store, domEl) {
    if (utils.config.getIsEnableAlamoInsider() && domEl) {
      const root = createRoot(domEl);
      root.render(
        <Provider store={store}>
          <Authenticated>
            {({ logged_in, keepMeLoggedIn, profileData }) => {
              const openSessionTimeoutModal = useActions(showSessionTimeoutModal);
              const openUpdateEmailAddressModal = useActions(showUpdateEmailAddressModal);
              // Redirect objects from AEM
              // authenticated_redirects -> Redirect mapping config for authenticated users
              // unauthenticated_redirects -> Redirect mapping config for unauthenticated users
              const { authenticated_redirects, unauthenticated_redirects } = utils.config.getAuthenticationRedirects();
              const { email_update_required } = profileData || {};
              const showEmailModal = utils.storageManager.SessionStorage.get(
                SESSION_STORAGE.SHOW_EMAIL_UPDATE_MODAL,
                SESSION_STORAGE.JSON_PARSE
              );

              if (logged_in && utils.config.isAuthHomePage() && email_update_required && showEmailModal) {
                setTimeout(() => {
                  openUpdateEmailAddressModal();
                }, 1000);
              }

              // Current resource path from AEM
              const currentPagePath = utils.config.getResourcePath();

              // Checks for specific windowObjectKeys for navigating to specific after login.
              // Currently checking only viewModifyCancel details / Confirmation
              const storedRedirect = localStorage.getItem(utils.config.goToSpecificPagePath);

              // Start timer for auth user
              logged_in && utils.timeoutRedirect.start(openSessionTimeoutModal);

              // If user is at auth-home-page (logged in) with keepMeLoggedIn should keep at auth-home-page
              logged_in &&
                keepMeLoggedIn &&
                utils.config.isAuthHomePage() &&
                utils.timeoutRedirect.setCustomRedirect(WINDOW_OBJECT_KEYS.AUTH_HOME_PAGE);

              // If user is logged in and the previous page was Confirmation/viewModifyCancel details
              // or If user is logged in and in a page path that have auth redirect,
              // fire loading overlay and redirect user to the path.
              const postLoginRedirect =
                utils.config.getRedirectUrl(storedRedirect) ?? authenticated_redirects?.[currentPagePath];

              if (logged_in && postLoginRedirect) {
                localStorage.removeItem(utils.config.goToSpecificPagePath);
                utils.dom.loadingOverlay(true);
                window.location.replace(postLoginRedirect);
              }

              // If user is not logged in and in a page path that have unauth redirect,
              // fire loading overlay and redirect user to the path.
              // `logged_in` must be false because null or undefined means that current
              // session fetch is incomplete.
              if (logged_in === false && unauthenticated_redirects?.[currentPagePath]) {
                utils.dom.loadingOverlay(true);
                window.location.replace(unauthenticated_redirects[currentPagePath]);
              }

              // Returning null as no visual component is needed.
              return null;
            }}
          </Authenticated>
          <SessionTimeoutModal />
          <UpdateEmailAddressModal />
          <EmailUpdatedSuccessModal />
        </Provider>
      );
    }
  },
};
